import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { workspaceAPI, analysisAPI, teamAPI, icpAPI, billingAPI, monitorAPI } from '../lib/api';

// Import user's content components
import InferenceOverview from '../components/inference/inference-overview';
import InferenceNewAnalysis from '../components/inference/inference-new-analysis';
import InferenceResults from '../components/inference/inference-results';
import InferenceRecentSearch, { GlobalSearch } from '../components/inference/inference-recent-search';
import { NotificationBell } from '../components/inference/notification-bell';
import InferenceMonitored from '../components/inference/inference-monitored';
import InferenceSavedArticles from '../components/inference/inference-saved-articles';
import InferenceBatch from '../components/inference/inference-batch';
import InferenceContacts from '../components/inference/inference-contacts';
import InferenceSettings from '../components/inference/inference-settings';
import InferenceTeamScreens from '../components/inference/inference-team-screens';
import InferenceTemplatesExports from '../components/inference/inference-templates-exports';
import HelpScreen from '../components/inference/inference-help';
import InferenceICPSetup from '../components/inference/inference-icp-setup';
import InferenceSavedSummaries from '../components/inference/inference-saved-summaries';

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const icons = {
  overview: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  analyze: "M12 4v16m8-8H4",
  articles: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  monitor: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z",
  recent: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  batch: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4",
  exports: "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  activity: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  notes: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  folders: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
  summaries: "M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z",
  help: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  settings: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
};

const NAV = [
  { id: "overview", label: "Overview", icon: icons.overview, tier: 1 },
  { id: "analyze", label: "New Analysis", icon: icons.analyze, tier: 1, isAction: true },
  { id: "divider1", divider: true },
  { id: "recent", label: "Recent Analyses", icon: icons.recent, tier: 1 },
  { id: "monitor", label: "Monitored Companies", icon: icons.monitor, tier: 1, badge: 3 },
  { id: "savedCompanies", label: "Saved Companies", icon: icons.companies || icons.recent, tier: 1 },
  { id: "articles", label: "Saved Articles", icon: icons.articles, tier: 1 },
  { id: "savedSummaries", label: "Saved Summaries", icon: icons.summaries, tier: 1 },
  { id: "contacts", label: "Contacts", icon: icons.contacts || icons.activity, tier: 1 },
  { id: "notes", label: "Decision Notes", icon: icons.notes, tier: 1 },
  { id: "batch", label: "Batch Analysis", icon: icons.batch, tier: 1 },
  { id: "exports", label: "Exports", icon: icons.exports, tier: 1 },
  { id: "divider2", divider: true, tierLabel: "TEAM · ORG", minTier: 2 },
  { id: "activity", label: "Team Activity", icon: icons.activity, tier: 2 },
  { id: "folders", label: "Shared Folders", icon: icons.folders, tier: 2 },
];

const BOTTOM_NAV = [
  { id: "help", label: "Get Help", icon: icons.help },
  { id: "settings", label: "Settings", icon: icons.settings },
];

const PLAN_COLORS = {
  Individual: { bg: "#161B27", text: "#6B7280", border: BORDER },
  Team: { bg: "#0D1829", text: "#3B82F6", border: "#1E3A6E" },
  Org: { bg: "#1A0A2E", text: "#A855F7", border: "#3B1F5E" },
};

/* ── Workspace tier label / badge mapping ── */
const WORKSPACE_TIER_LABELS = {
  free: "Free",
  individual: "Solo",
  individual_plus: "Solo+",
  team: "Team Pro",
  org_standard: "Org Standard",
  org_plus: "Org Plus",
  enterprise: "Enterprise",
};
const WORKSPACE_TIER_BADGE = {
  org_standard: { bg: "#0D1829", text: "#3B82F6", border: "#1E3A6E" },
  org_plus: { bg: "#1A0A2E", text: "#A855F7", border: "#3B1F5E" },
  enterprise: { bg: "#1F1A05", text: "#F59E0B", border: "#78350F" },
};
const tierBadgeStyle = (tier) => WORKSPACE_TIER_BADGE[tier] || { bg: "#161B27", text: "#9CA3AF", border: BORDER };

/* ── Workspace Switcher (TopBar) ──
 * Renders nothing for single-workspace users.
 * Shows current workspace name + chevron, dropdown lists all memberships
 * with checkmark on the active one, tier badge, and seats X / Y.
 */
const WorkspaceSwitcher = ({ workspaces, activeWorkspaceId, onSwitch }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Outside click + ESC close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!workspaces || workspaces.length <= 1) return null;
  const active = workspaces.find(w => w.workspace_id === activeWorkspaceId) || workspaces[0];
  if (!active) return null;
  const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : s || "");

  return (
    <div ref={containerRef} data-testid="workspace-switcher" style={{ position: "relative" }}>
      <button
        data-testid="workspace-switcher-trigger"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={active.name}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          background: SURFACE_2, color: TEXT_PRIMARY,
          border: `1px solid ${BORDER}`, borderRadius: 8,
          padding: "6px 10px 6px 12px", fontSize: 12.5, fontWeight: 600,
          cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          transition: "border-color 0.15s, background 0.15s",
          maxWidth: 240,
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "#2D3748"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; }}
      >
        <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={1.8} style={{ flexShrink: 0 }}>
          <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        <span data-testid="workspace-switcher-active-name" className="ws-switcher-name" style={{
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          letterSpacing: "-0.01em",
        }}>{active.name}</span>
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={2.2} strokeLinecap="round" style={{ flexShrink: 0, transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div
          data-testid="workspace-switcher-dropdown"
          role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            minWidth: 280, maxWidth: 360,
            background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)", padding: 6, zIndex: 70,
          }}
        >
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", padding: "6px 10px 4px", letterSpacing: "0.05em" }}>WORKSPACES</div>
          {workspaces.map(ws => {
            const isActive = ws.workspace_id === activeWorkspaceId;
            const badge = tierBadgeStyle(ws.tier);
            const seatsTotal = ws.members_limit;
            const seatsUsed = ws.members_used != null ? ws.members_used : 1;
            return (
              <button
                key={ws.workspace_id}
                data-testid={`workspace-option-${ws.workspace_id}`}
                role="option"
                aria-selected={isActive}
                onClick={() => { onSwitch(ws.workspace_id); setOpen(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", textAlign: "left",
                  padding: "10px 10px", borderRadius: 8,
                  background: "transparent", border: "none", cursor: "pointer",
                  color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif",
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = SURFACE_2; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ width: 14, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                  {isActive ? (
                    <svg data-testid={`workspace-option-check-${ws.workspace_id}`} width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ws.name}</span>
                    <span style={{
                      fontSize: 9.5, fontWeight: 600, fontFamily: "'DM Mono', monospace",
                      color: badge.text, background: badge.bg,
                      border: `1px solid ${badge.border}`, borderRadius: 4,
                      padding: "1.5px 6px", letterSpacing: "0.03em", flexShrink: 0,
                    }}>{(WORKSPACE_TIER_LABELS[ws.tier] || ws.tier).toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 3, fontFamily: "'DM Mono', monospace" }}>
                    {seatsUsed} / {seatsTotal} seats
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Sidebar ── */
const Sidebar = ({ collapsed, onToggle, activeNav, onNav, userTier, user, onLogout, mobileOpen, monitorCount }) => {
  const planStyle = PLAN_COLORS[userTier] || PLAN_COLORS.Individual;
  // Map sidebar UI labels (Individual/Team/Org) to numeric tiers for nav locks.
  // Individual covers free → tier_2_5; Team is tier_3; Org covers tier_4 + Enterprise.
  const tierNum = { Individual: 1, Team: 2, Org: 3 }[userTier] || 1;
  const initials = user?.full_name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <aside data-testid="app-sidebar" data-mobile-open={mobileOpen ? "true" : "false"} style={{
      width: collapsed ? 56 : 220, background: SURFACE,
      borderRight: `1px solid ${BORDER}`, display: "flex", flexDirection: "column",
      height: "100vh", position: "fixed", left: 0, top: 0, zIndex: 50,
      transition: "width 0.22s cubic-bezier(.4,0,.2,1)", overflow: "hidden",
    }}>
      <div style={{ height: 56, display: "flex", flexDirection: "row", alignItems: "center", justifyContent: collapsed ? "flex-end" : "space-between", gap: 0, padding: collapsed ? "0" : "0 16px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        {!collapsed && (
          <div
            data-testid="app-sidebar-logo"
            onClick={() => onNav('overview')}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <img
              src="/inference-logo.svg"
              alt="Inference"
              style={{ height: 68, width: "auto", display: "block" }}
            />
          </div>
        )}
        {collapsed && (
          <div
            data-testid="app-sidebar-logo-collapsed"
            onClick={() => onNav('overview')}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
          >
            <img
              src="/inference-icon.svg"
              alt="Inference"
              style={{ height: 28, width: 28, display: "block" }}
            />
          </div>
        )}
        <button onClick={onToggle} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_DIM, padding: 4, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            {collapsed ? <path d="M13 5l7 7-7 7M5 5l7 7-7 7" /> : <path d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />}
          </svg>
        </button>
      </div>

      <nav style={{ flex: 1, overflowY: "auto", padding: "8px 8px", display: "flex", flexDirection: "column", gap: 1 }}>
        {NAV.map((item) => {
          if (item.divider) {
            return (
              <div key={item.id} style={{ margin: "8px 0 4px", padding: "0 4px" }}>
                {!collapsed && item.tierLabel && (
                  <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.1em", color: TEXT_DIM, fontFamily: "'DM Mono', monospace", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ flex: 1, height: 1, background: BORDER }} />
                    {item.tierLabel}
                    <span style={{ flex: 1, height: 1, background: BORDER }} />
                  </span>
                )}
                {collapsed && <div style={{ height: 1, background: BORDER, margin: "4px 0" }} />}
              </div>
            );
          }

          const locked = item.tier > 1 && tierNum < item.tier;
          const isActive = activeNav === item.id;

          return (
            <a
              key={item.id}
              href={locked ? undefined : `/app?nav=${item.id}`}
              onClick={(e) => { e.preventDefault(); if (!locked) onNav(item.id); }}
              data-testid={`nav-${item.id}`}
              title={collapsed ? item.label : undefined}
              style={{
                display: "flex", alignItems: "center", gap: collapsed ? 0 : 10,
                padding: collapsed ? "9px 9px" : "9px 10px", borderRadius: 8,
                background: isActive ? `${ACCENT}18` : item.isAction ? `${ACCENT}0F` : "transparent",
                color: locked ? TEXT_DIM : isActive ? ACCENT : item.isAction ? ACCENT : "#9CA3AF",
                cursor: locked ? "not-allowed" : "pointer", fontFamily: "'DM Sans', sans-serif",
                fontSize: 13, fontWeight: isActive ? 600 : 500, opacity: locked ? 0.45 : 1,
                transition: "all 0.15s", width: "100%", textAlign: "left", position: "relative",
                justifyContent: collapsed ? "center" : "flex-start", outline: "none",
                textDecoration: "none",
              }}
            >
              {isActive && <div style={{ position: "absolute", left: 0, top: "20%", bottom: "20%", width: 3, borderRadius: "0 3px 3px 0", background: ACCENT }} />}
              <svg width={17} height={17} viewBox="0 0 24 24" fill={item.isAction && !isActive ? ACCENT : "none"} stroke="currentColor" strokeWidth={isActive ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d={item.icon} />
                {item.id === "monitor" && <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />}
              </svg>
              {!collapsed && <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>}
              {!collapsed && item.id === "monitor" && monitorCount > 0 && !locked && (
                <span data-testid="nav-monitor-badge" style={{ background: "#F59E0B22", color: "#F59E0B", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 100, fontFamily: "'DM Mono', monospace" }}>{monitorCount}</span>
              )}
              {!collapsed && item.id !== "monitor" && item.badge && !locked && (
                <span style={{ background: "#F59E0B22", color: "#F59E0B", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 100, fontFamily: "'DM Mono', monospace" }}>{item.badge}</span>
              )}
            </a>
          );
        })}
      </nav>

      <div style={{ borderTop: `1px solid ${BORDER}`, padding: "8px 8px", flexShrink: 0 }}>
        {BOTTOM_NAV.map(item => {
          const isActive = activeNav === item.id;
          return (
            <a
              key={item.id}
              href={`/app?nav=${item.id}`}
              onClick={(e) => { e.preventDefault(); onNav(item.id); }}
              data-testid={`nav-${item.id}`}
              title={collapsed ? item.label : undefined}
              style={{
                display: "flex", alignItems: "center", gap: collapsed ? 0 : 10,
                padding: collapsed ? "8px 9px" : "8px 10px", borderRadius: 8,
                background: isActive ? `${ACCENT}18` : "transparent",
                color: isActive ? ACCENT : TEXT_MUTED, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                fontSize: 13, fontWeight: isActive ? 600 : 400, width: "100%", transition: "all 0.15s",
                justifyContent: collapsed ? "center" : "flex-start", outline: "none", marginBottom: 2,
                textDecoration: "none",
              }}
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d={item.icon} />
              </svg>
              {!collapsed && <span>{item.label}</span>}
            </a>
          );
        })}
      </div>

      <div ref={menuRef} style={{ padding: collapsed ? "10px 8px" : "12px 12px", borderTop: `1px solid ${BORDER}`, flexShrink: 0, position: "relative" }}>
        <button
          data-testid="user-pill-btn"
          onClick={() => setMenuOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px",
            borderRadius: 9, background: menuOpen ? `${ACCENT}12` : SURFACE_2,
            border: `1px solid ${menuOpen ? ACCENT : BORDER}`, cursor: "pointer",
            justifyContent: collapsed ? "center" : "flex-start",
            width: "100%", fontFamily: "'DM Sans', sans-serif",
            transition: "all 0.15s",
          }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
            background: `linear-gradient(135deg, ${ACCENT} 0%, #1D4ED8 100%)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#fff",
          }}>{initials}</div>
          {!collapsed && (
            <div style={{ flex: 1, overflow: "hidden", textAlign: "left" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user?.full_name || "User"}
              </div>
              <div style={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user?.email || ""}
              </div>
            </div>
          )}
          {!collapsed && (
            <span style={{
              fontSize: 9, fontWeight: 600, color: planStyle.text,
              background: planStyle.bg, border: `1px solid ${planStyle.border}`,
              borderRadius: 4, padding: "2px 6px", fontFamily: "'DM Mono', monospace", flexShrink: 0,
            }}>{userTier.toUpperCase()}</span>
          )}
        </button>

        {menuOpen && (
          <div
            data-testid="user-menu-dropdown"
            role="menu"
            style={{
              position: "absolute",
              bottom: "calc(100% - 4px)",
              left: 12, right: 12,
              background: SURFACE_2, border: `1px solid ${BORDER}`,
              borderRadius: 10,
              padding: 6,
              boxShadow: "0 20px 50px -10px rgba(0,0,0,0.6)",
              zIndex: 60,
              display: "flex", flexDirection: "column", gap: 2,
            }}
          >
            {[
              { id: "profile", label: "Profile", testid: "user-menu-profile",
                icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
              { id: "settings", label: "Settings", testid: "user-menu-settings",
                icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM12 15a3 3 0 100-6 3 3 0 000 6z" },
              { divider: true },
              { id: "signout", label: "Sign Out", testid: "user-menu-signout", danger: true,
                icon: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" },
            ].map((it, i) => {
              if (it.divider) return <div key={`d${i}`} style={{ height: 1, background: BORDER, margin: "4px 6px" }} />;
              return (
                <button
                  key={it.id}
                  data-testid={it.testid}
                  onClick={() => {
                    setMenuOpen(false);
                    if (it.id === "profile") onNav("settings");
                    else if (it.id === "settings") onNav("settings");
                    else if (it.id === "signout") onLogout();
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 10px", borderRadius: 7, border: "none",
                    background: "transparent",
                    color: it.danger ? "#F87171" : TEXT_PRIMARY,
                    fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
                    cursor: "pointer", textAlign: "left",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = it.danger ? "#7F1D1D22" : `${ACCENT}14`}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                    <path d={it.icon} />
                  </svg>
                  {it.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
};

/* ── Top Bar ── */
const TopBar = ({ sidebarCollapsed, activeNav, onNav, onMobileMenuToggle, workspaces, activeWorkspaceId, onSwitchWorkspace, onViewAnalysis }) => {
  const labels = {
    overview: "Overview", analyze: "New Analysis", articles: "Saved Articles",
    monitor: "Monitored Companies", recent: "Recent Analyses", activity: "Team Activity",
    notes: "Decision Notes", folders: "Shared Folders", summaries: "Templates & Exports",
    savedSummaries: "Saved Summaries", savedCompanies: "Saved Companies",
    batch: "Batch Analysis", exports: "Exports", results: "Results",
    contacts: "Contacts",
    help: "Get Help", settings: "Settings",
  };

  return (
    <header data-testid="app-topbar" style={{
      position: "fixed", top: 0, left: sidebarCollapsed ? 56 : 220, right: 0,
      height: 56, background: `${BG}EE`, backdropFilter: "blur(10px)",
      borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center",
      padding: "0 28px", justifyContent: "space-between", zIndex: 40,
      transition: "left 0.22s cubic-bezier(.4,0,.2,1)",
    }}>
      <style>{`
        @media (max-width: 640px) {
          .ws-switcher-name { max-width: 110px; }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          data-mobile-hamburger
          data-testid="mobile-hamburger-btn"
          onClick={onMobileMenuToggle}
          aria-label="Open menu"
          style={{
            display: "none",
            alignItems: "center", justifyContent: "center",
            width: 40, height: 40, borderRadius: 8,
            background: "transparent", border: `1px solid ${BORDER}`,
            color: TEXT_PRIMARY, cursor: "pointer",
          }}
        >
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <span style={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.015em" }}>
          {labels[activeNav] || "Inference"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <WorkspaceSwitcher
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspaceId}
          onSwitch={onSwitchWorkspace}
        />
        <NotificationBell onViewAnalysis={onViewAnalysis} onNavigate={onNav} />
        <button data-testid="topbar-analyze-btn" onClick={() => onNav('analyze')} style={{
          display: "flex", alignItems: "center", gap: 7, background: ACCENT, color: "#fff",
          border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600,
          cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "background 0.2s", letterSpacing: "-0.01em",
        }}>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 4v16m8-8H4" /></svg>
          Analyze
        </button>
      </div>
    </header>
  );
};

/* ── Onboarding Persona Selector ──
   One-time first-login screen. Personalizes the default landing tab for
   every analysis the user runs. Idempotent via users.onboarding_persona_completed.
   All 11 tabs remain visible to every persona — this only affects which tab
   shows first when an analysis opens. */
const PERSONA_OPTIONS = [
  {
    id: "smb_sales",
    title: "SMB Sales",
    description: "I prepare for prospect meetings and manage my own pipeline",
    // briefcase
    icon: "M20 7H4a2 2 0 00-2 2v8a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2",
  },
  {
    id: "enterprise_ae",
    title: "Enterprise AE",
    description: "I manage complex multi-stakeholder deals at large companies",
    // building / org chart
    icon: "M3 21h18M5 21V7a2 2 0 012-2h10a2 2 0 012 2v14M9 21V9m6 12V9M9 13h.01M15 13h.01M9 17h.01M15 17h.01",
  },
  {
    id: "pe_bd_corp_dev",
    title: "PE / BD / Corp Dev",
    description: "I evaluate companies for investment, acquisition, or partnership",
    // chart with magnifying lens
    icon: "M11 19a8 8 0 100-16 8 8 0 000 16zm10 2l-4.35-4.35M7 14l3-3 3 3 4-4",
  },
  {
    id: "founder_executive",
    title: "Founder / Executive",
    description: "I prepare for investor, partner, or board meetings",
    // rocket
    icon: "M5 13l-1 7 7-1 9-9-6-6-9 9zM14 6l6 6M9 14l-2 5 5-2",
  },
];

const OnboardingPersonaSelector = ({ onComplete }) => {
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (persona) => {
    if (saving) return;
    setSaving(true);
    try {
      const { authAPI } = await import('../lib/api');
      await authAPI.setOnboardingPersona(persona);
      onComplete(persona);
    } catch (e) {
      toast.error("Could not save your selection. Try again.");
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="onboarding-persona-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6, 8, 15, 0.92)",
        backdropFilter: "blur(8px)",
        zIndex: 9999,
        overflowY: "auto",
        padding: "60px 24px 40px",
        fontFamily: "'DM Sans', sans-serif",
        color: TEXT_PRIMARY,
      }}
    >
      <style>{`
        @media (max-width: 767px) {
          [data-testid="persona-card-grid"] { grid-template-columns: 1fr !important; }
          [data-testid="onboarding-persona-continue"] { width: 100% !important; }
        }
      `}</style>

      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <h1 style={{
          fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em",
          marginBottom: 10, color: TEXT_PRIMARY,
        }}>
          What best describes how you will use Inference?
        </h1>
        <p style={{
          fontSize: 14, color: TEXT_MUTED, marginBottom: 32, lineHeight: 1.55,
        }}>
          We will personalize your experience based on your role.
          You can change this anytime in Settings.
        </p>

        <div
          data-testid="persona-card-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginBottom: 28,
          }}
        >
          {PERSONA_OPTIONS.map(opt => {
            const isSelected = selected === opt.id;
            return (
              <button
                key={opt.id}
                data-testid={`persona-card-${opt.id}`}
                data-selected={isSelected ? "true" : "false"}
                onClick={() => setSelected(opt.id)}
                disabled={saving}
                style={{
                  textAlign: "left",
                  background: SURFACE,
                  border: `1px solid ${isSelected ? ACCENT : BORDER}`,
                  borderRadius: 12,
                  padding: "18px 18px 16px",
                  cursor: saving ? "wait" : "pointer",
                  position: "relative",
                  color: TEXT_PRIMARY,
                  transition: "border-color 0.15s, background 0.15s",
                  fontFamily: "'DM Sans', sans-serif",
                  outline: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
                onMouseEnter={e => {
                  if (!isSelected) e.currentTarget.style.borderColor = "#2D3748";
                }}
                onMouseLeave={e => {
                  if (!isSelected) e.currentTarget.style.borderColor = BORDER;
                }}
              >
                {/* Checkmark — only when selected */}
                {isSelected && (
                  <span
                    data-testid={`persona-card-check-${opt.id}`}
                    style={{
                      position: "absolute",
                      top: 12, right: 12,
                      width: 22, height: 22, borderRadius: "50%",
                      background: ACCENT,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <svg width={12} height={12} viewBox="0 0 24 24" fill="none"
                         stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                )}

                <span style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: isSelected ? `${ACCENT}22` : SURFACE_2,
                  border: `1px solid ${isSelected ? `${ACCENT}55` : BORDER}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.15s, border-color 0.15s",
                }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
                       stroke={isSelected ? ACCENT : TEXT_MUTED}
                       strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                    <path d={opt.icon} />
                  </svg>
                </span>

                <div style={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY }}>
                  {opt.title}
                </div>
                <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5 }}>
                  {opt.description}
                </div>
              </button>
            );
          })}
        </div>

        {/* Continue + Skip */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          {selected && (
            <button
              data-testid="onboarding-persona-continue"
              onClick={() => submit(selected)}
              disabled={saving}
              style={{
                background: ACCENT,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "11px 28px",
                fontSize: 14,
                fontWeight: 600,
                cursor: saving ? "wait" : "pointer",
                fontFamily: "'DM Sans', sans-serif",
                minWidth: 180,
                transition: "background 0.15s, opacity 0.15s",
                opacity: saving ? 0.7 : 1,
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.background = "#1D4ED8"; }}
              onMouseLeave={e => { if (!saving) e.currentTarget.style.background = ACCENT; }}
            >
              {saving ? "Saving..." : "Continue"}
            </button>
          )}
          <button
            data-testid="onboarding-persona-skip"
            onClick={() => submit("skip")}
            disabled={saving}
            style={{
              background: "transparent",
              border: "none",
              color: TEXT_MUTED,
              fontSize: 13,
              cursor: saving ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
              textDecoration: "underline",
              textUnderlineOffset: 3,
              transition: "color 0.15s",
            }}
            onMouseEnter={e => { if (!saving) e.currentTarget.style.color = TEXT_PRIMARY; }}
            onMouseLeave={e => { if (!saving) e.currentTarget.style.color = TEXT_MUTED; }}
          >
            Skip for now — I will explore on my own
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Trial Progress Strip ── */
const TrialProgressStrip = ({ workspaceId, rawTier, onUpgrade, sidebarCollapsed }) => {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Only fetch when on free tier — saves an API call for paid users.
      if (rawTier !== "free") return;
      try {
        const r = await billingAPI.usage(workspaceId);
        if (!cancelled) setUsage(r.data);
      } catch (e) {
        // Strip is informational only — silently no-op on failure.
        if (!cancelled) setUsage(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [workspaceId, rawTier]);

  if (rawTier !== "free" || !usage) return null;
  const phase = usage.phase;
  // Only render during the active trial window — drop the strip on day 30+
  // when the user is back on standard monthly logic.
  if (!phase || !phase.startsWith("trial_phase_")) return null;

  const daysSince = usage.days_since_signup ?? 0;
  const dayDisplay = Math.min(daysSince + 1, 30);
  const used = usage.used ?? 0;
  const cap = usage.cap ?? 0;
  const isPhase1 = phase === "trial_phase_7";
  const daysToPhase2 = Math.max(0, 7 - daysSince);
  const daysToEnd = Math.max(0, 30 - daysSince);

  return (
    <div
      data-testid="trial-progress-strip"
      style={{
        position: "fixed",
        top: 56,
        left: sidebarCollapsed ? 56 : 220,
        right: 0,
        zIndex: 50,
        background: "#0F1422",
        borderBottom: "1px solid #1E2433",
        borderLeft: "3px solid #F59E0B",
        padding: "8px 20px",
        display: "flex",
        alignItems: "center",
        gap: 18,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 12,
        color: "#D1D5DB",
        transition: "left 0.22s cubic-bezier(.4,0,.2,1)",
      }}
    >
      <span data-testid="trial-strip-day" style={{ fontFamily: "'DM Mono', monospace", color: "#F59E0B", letterSpacing: "0.05em" }}>
        DAY {dayDisplay} OF 30
      </span>
      <span style={{ color: "#374151" }}>·</span>
      <span data-testid="trial-strip-usage">
        <strong style={{ color: "#F9FAFB" }}>{used} of {cap}</strong> analyses used
      </span>
      <span style={{ color: "#374151" }}>·</span>
      <span data-testid="trial-strip-hint" style={{ color: "#9CA3AF" }}>
        {isPhase1 ? (
          daysToPhase2 === 0
            ? "Phase 2 unlocks today — 7 more analyses"
            : `Phase 2 unlocks in ${daysToPhase2} day${daysToPhase2 === 1 ? "" : "s"} — 7 more analyses`
        ) : (
          daysToEnd === 0
            ? "Trial ends today"
            : `${daysToEnd} day${daysToEnd === 1 ? "" : "s"} left in your trial`
        )}
      </span>
      <span style={{ flex: 1 }} />
      <button
        data-testid="trial-strip-upgrade-btn"
        onClick={onUpgrade}
        style={{
          background: "#F59E0B22",
          border: "1px solid #F59E0B55",
          borderRadius: 6,
          padding: "4px 12px",
          fontSize: 11,
          fontWeight: 600,
          color: "#F59E0B",
          cursor: "pointer",
          letterSpacing: "0.04em",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >Upgrade →</button>
    </div>
  );
};

/* ── Main App Page ── */
export default function AppMain() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("overview");

  // Honour ?nav=<id> on initial load / direct links (e.g. middle-click from sidebar)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const navParam = params.get("nav");
    if (navParam) setActiveNav(navParam);
  }, []);

  // Scroll to top whenever the user navigates to a different tab/screen.
  // Without this, switching from a long scrolled page (e.g. a long
  // Recent Analyses list) to a new section leaves the new screen scrolled
  // mid-way down, hiding its header/CTA. Behaviour: instant scroll (no
  // smooth animation) so it feels like a real page change.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch (e) { /* ignore */ }
  }, [activeNav]);
  const [teamId, setTeamId] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showICPSetup, setShowICPSetup] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  // Live count for the "Monitored Companies" sidebar badge. Loaded on
  // workspace change and re-fetched whenever the user navigates back to the
  // monitor tab so creates/deletes flow through without a page refresh.
  const [monitorCount, setMonitorCount] = useState(0);

  // Global Cmd+K search — mounted at the app-shell level so the shortcut
  // works from every screen, not just Recent Analyses. Screen-scoped
  // instances still work independently; this is the always-on fallback.
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
      if (e.key === "Escape") setGlobalSearchOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const refreshMonitorCount = useCallback(async () => {
    try {
      const res = await monitorAPI.list();
      const arr = Array.isArray(res?.data) ? res.data : (res?.data?.monitors || []);
      setMonitorCount(arr.length);
    } catch (e) {
      // Silent: badge just won't render. Don't toast for a counter.
    }
  }, []);

  const handleSaveAnalysis = useCallback((analysisId) => {
    setSavedIds(prev => {
      const next = new Set(prev);
      if (next.has(analysisId)) next.delete(analysisId);
      else next.add(analysisId);
      return next;
    });
  }, []);

  // Fetch workspace + team on mount, check ICP.
  // Gated on `user` being non-null so we don't fire 401-bound calls before
  // AuthContext.checkAuth() has hydrated the session token.
  useEffect(() => {
    if (!user) return;
    const init = async () => {
      try {
        const wsRes = await workspaceAPI.list();
        if (wsRes.data && wsRes.data.length > 0) {
          setWorkspaces(wsRes.data);
          // Prefer previously selected workspace from localStorage if still a member.
          // Read new key first; fall back to legacy key for backwards compatibility.
          const stored = localStorage.getItem('inference_active_workspace_id') || localStorage.getItem('active_workspace_id');
          const match = stored && wsRes.data.find(w => w.workspace_id === stored);
          const chosen = match ? match.workspace_id : wsRes.data[0].workspace_id;
          if (!match && stored) {
            // Stale stored ID — the user is no longer a member. Clear both keys.
            try { localStorage.removeItem('inference_active_workspace_id'); } catch (e) { /* ignore */ }
            try { localStorage.removeItem('active_workspace_id'); } catch (e) { /* ignore */ }
          }
          setWorkspaceId(chosen);
          // Persist under both keys so the existing axios interceptor (which reads
          // 'active_workspace_id') keeps working without a coordinated change.
          localStorage.setItem('inference_active_workspace_id', chosen);
          localStorage.setItem('active_workspace_id', chosen);
        }
      } catch (err) {
        console.error('Failed to fetch workspace:', err);
      }
      try {
        const res = await teamAPI.list();
        if (res.data && res.data.length > 0) {
          setTeamId(res.data[0].team_id);
        } else {
          const newTeam = await teamAPI.create("My Team");
          setTeamId(newTeam.data.team_id);
        }
      } catch (err) {
        console.error('Failed to fetch/create team:', err);
      }
      // Check if ICP is configured
      if (user && !user.icp_configured) {
        setShowICPSetup(true);
      }
    };
    init();
  }, [user]);

  const handleAnalysisComplete = useCallback((analysisData) => {
    setSelectedAnalysis(analysisData);
    setActiveNav("results");
  }, []);

  const handleViewAnalysis = useCallback(async (analysisId) => {
    if (!workspaceId) return;
    try {
      const res = await analysisAPI.get(workspaceId, analysisId);
      setSelectedAnalysis(res.data);
      setActiveNav("results");
    } catch (err) {
      console.error('Failed to fetch analysis:', err);
    }
  }, [workspaceId]);

  // Deep-link consumer: on mount (once workspaceId is available) check whether
  // the URL path is /app/analysis/<id> — or whether ProtectedRoute stashed
  // such a path in sessionStorage during a pre-auth redirect — and open that
  // analysis directly. Existing ?nav=<id> navigation is unaffected because
  // this only fires when the URL pathname matches /app/analysis/.
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (!workspaceId) return;
    if (deepLinkConsumedRef.current) return;

    // Resolve the candidate path: either a stashed post-login redirect, or
    // the live URL the user is on right now.
    let candidate = "";
    try {
      const stashed = sessionStorage.getItem("inference_post_login_redirect");
      if (stashed) {
        candidate = stashed;
        sessionStorage.removeItem("inference_post_login_redirect");
      }
    } catch (e) { /* ignore */ }
    if (!candidate && typeof window !== "undefined") {
      candidate = window.location.pathname + (window.location.search || "");
    }

    // Match /app/analysis/<id> (id = non-slash, non-query characters).
    const m = candidate.match(/^\/app\/analysis\/([^/?#]+)/);
    if (!m) return;
    const aid = m[1];
    deepLinkConsumedRef.current = true;

    (async () => {
      try {
        const res = await analysisAPI.get(workspaceId, aid);
        setSelectedAnalysis(res.data);
        setActiveNav("results");
        // Clean the URL back to /app so a refresh doesn't re-trigger this
        // path-based load (the in-memory state is now the source of truth).
        try { window.history.replaceState(null, "", "/app"); } catch (e) { /* ignore */ }
      } catch (err) {
        toast.error("Analysis not found or you do not have access");
        try { window.history.replaceState(null, "", "/app"); } catch (e) { /* ignore */ }
      }
    })();
  }, [workspaceId]);

  // Refresh the "Monitored Companies" sidebar badge count whenever the
  // active workspace changes or the user navigates to the monitor tab
  // (so creating/deleting a monitor on that screen reflects in the badge
  // when they leave and come back). Cheap GET — no pagination needed
  // because the same endpoint already powers the listing screen.
  useEffect(() => {
    if (!workspaceId) return;
    refreshMonitorCount();
  }, [workspaceId, activeNav, refreshMonitorCount]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNav = (id) => {
    setActiveNav(id);
    // Reset scroll on internal nav change too — AppMain switches screens
    // via component state (not URL routes) so the global ScrollToTop hook
    // in App.js doesn't fire for these transitions.
    try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch { window.scrollTo(0, 0); }
    document.querySelectorAll('[data-scroll-container="true"]').forEach(el => {
      try { el.scrollTop = 0; } catch { /* ignore */ }
    });
  };

  const handleSwitchWorkspace = useCallback((newWorkspaceId) => {
    if (!newWorkspaceId || newWorkspaceId === workspaceId) return;
    try {
      localStorage.setItem('inference_active_workspace_id', newWorkspaceId);
      localStorage.setItem('active_workspace_id', newWorkspaceId);
    } catch (e) { /* ignore */ }
    // Hard reload so every cached query, in-memory analysis, and route-scoped
    // request is rebuilt against the freshly-active workspace.
    window.location.reload();
  }, [workspaceId]);

  // Derive the sidebar tier label from the ACTIVE WORKSPACE document, not
  // from the user document. Each workspace carries its own tier_id and
  // feature matrix; switching workspaces must change feature gating.
  // Mapping (per spec): free / tier_1 / tier_2 / tier_2_5 → Individual,
  //                     tier_3 → Team,
  //                     tier_4_std / tier_4_plus / enterprise → Org.
  const userTier = (() => {
    const active = workspaces.find(w => w.workspace_id === workspaceId);
    const t = (active?.tier || "").toString();
    if (["tier_4_std", "tier_4_plus", "org_standard", "org_plus", "enterprise"].includes(t)) return "Org";
    if (["tier_3", "team"].includes(t)) return "Team";
    return "Individual";
  })();

  // Raw workspace tier — used to decide whether to render the trial strip.
  const rawWorkspaceTier = (() => {
    const active = workspaces.find(w => w.workspace_id === workspaceId);
    return (active?.tier || "").toString();
  })();

  const renderContent = () => {
    switch (activeNav) {
      case 'overview': return <InferenceOverview onViewAnalysis={handleViewAnalysis} workspaceId={workspaceId} userName={user?.full_name} onNavigate={handleNav} savedIds={savedIds} onSaveAnalysis={handleSaveAnalysis} />;
      case 'analyze': return <InferenceNewAnalysis workspaceId={workspaceId} onAnalysisComplete={handleAnalysisComplete} />;
      case 'results': return <InferenceResults analysis={selectedAnalysis} workspaceId={workspaceId} onNavigate={handleNav} savedIds={savedIds} onSaveAnalysis={handleSaveAnalysis} />;
      case 'recent': return <InferenceRecentSearch onViewAnalysis={handleViewAnalysis} workspaceId={workspaceId} savedIds={savedIds} onSaveAnalysis={handleSaveAnalysis} pageTitle="Recent Analyses" onNavigate={setActiveNav} />;
      case 'savedCompanies': return <InferenceRecentSearch onViewAnalysis={handleViewAnalysis} workspaceId={workspaceId} savedIds={savedIds} onSaveAnalysis={handleSaveAnalysis} savedOnly pageTitle="Saved Companies" onNavigate={setActiveNav} />;
      case 'monitor': return <InferenceMonitored onViewAnalysis={handleViewAnalysis} workspaceId={workspaceId} />;
      case 'articles': return <InferenceSavedArticles />;
      case 'batch': return <InferenceBatch workspaceId={workspaceId} onViewAnalysis={handleViewAnalysis} />;
      case 'exports': return <InferenceTemplatesExports workspaceId={workspaceId} onViewAnalysis={handleViewAnalysis} />;
      case 'contacts': return <InferenceContacts />;
      case 'savedSummaries': return <InferenceSavedSummaries workspaceId={workspaceId} />;
      case 'icpSetup': return <InferenceICPSetup existingProfile={{}} onComplete={() => { setShowICPSetup(false); setActiveNav("overview"); }} />;
      case 'activity': return <InferenceTeamScreens teamId={teamId} workspaceId={workspaceId} initialScreen="activity" />;
      case 'notes': return <InferenceTeamScreens teamId={teamId} workspaceId={workspaceId} initialScreen="notes" />;
      case 'folders': return <InferenceTeamScreens teamId={teamId} workspaceId={workspaceId} initialScreen="folders" />;
      case 'summaries': return <InferenceTemplatesExports workspaceId={workspaceId} onViewAnalysis={handleViewAnalysis} />;
      case 'help': return <HelpScreen />;
      case 'settings': return <InferenceSettings user={user} />;
      default: return <InferenceOverview onViewAnalysis={handleViewAnalysis} workspaceId={workspaceId} onNavigate={handleNav} />;
    }
  };

  // Show ICP setup for first-time users
  if (showICPSetup) {
    return (
      <InferenceICPSetup
        onComplete={() => {
          setShowICPSetup(false);
          if (checkAuth) checkAuth();
        }}
      />
    );
  }

  // One-time onboarding persona selector — fires only when the user has
  // never seen it. Existing accounts were backfilled to completed=true so
  // they never see this screen. The selector renders ON TOP of the
  // dashboard; the dashboard mounts behind it so the user lands on it
  // immediately after Continue or Skip.
  const showPersonaSelector = !!user && user.onboarding_persona_completed === false;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, minHeight: "100vh", color: TEXT_PRIMARY }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        button { outline: none; }
      `}</style>

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
        activeNav={activeNav}
        onNav={(id) => { handleNav(id); setMobileNavOpen(false); }}
        userTier={userTier}
        user={user}
        onLogout={handleLogout}
        mobileOpen={mobileNavOpen}
        monitorCount={monitorCount}
      />

      {/* Mobile backdrop — CSS controls visibility (hidden on desktop) */}
      {mobileNavOpen && (
        <div
          data-mobile-backdrop
          data-testid="mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          style={{ display: "none" }}
        />
      )}

      <TopBar
        sidebarCollapsed={collapsed}
        activeNav={activeNav}
        onNav={handleNav}
        onMobileMenuToggle={() => setMobileNavOpen(o => !o)}
        workspaces={workspaces}
        activeWorkspaceId={workspaceId}
        onSwitchWorkspace={handleSwitchWorkspace}
        onViewAnalysis={handleViewAnalysis}
      />

      <main data-testid="app-main-content" style={{
        marginLeft: collapsed ? 56 : 220,
        paddingTop: rawWorkspaceTier === "free" ? 96 : 56,
        minHeight: "100vh",
        background: BG,
        transition: "margin-left 0.22s cubic-bezier(.4,0,.2,1), padding-top 0.2s",
      }}>
        <TrialProgressStrip
          workspaceId={workspaceId}
          rawTier={rawWorkspaceTier}
          sidebarCollapsed={collapsed}
          onUpgrade={() => navigate("/pricing")}
        />
        {renderContent()}
      </main>

      {/* One-time onboarding persona selector — overlays the dashboard on
          first login only. Dismisses on Continue or Skip and never returns. */}
      {showPersonaSelector && (
        <OnboardingPersonaSelector
          onComplete={() => { if (checkAuth) checkAuth(); }}
        />
      )}

      {/* Always-on Cmd+K global search. */}
      {globalSearchOpen && (
        <GlobalSearch
          onClose={() => setGlobalSearchOpen(false)}
          onViewAnalysis={handleViewAnalysis}
          onNavigate={(section) => { setActiveNav(section); setGlobalSearchOpen(false); }}
        />
      )}
    </div>
  );
}
